import React, { useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import { circle } from '@galo-monitor/icons'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { BreedingActions } from '@galo-monitor/stores/breeding'
import DataTable from '@galo-monitor/web-components/DataTable'
import Icon from '@galo-monitor/web-components/Icon'
import Screen from '@galo-monitor/web-components/Screen'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import BreedingModal from '@galo-monitor/web-containers/modals/root/BreedingModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const BreedingsScreen = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const { data: breedings, mutate, isValidating } = useGaloMonitorApi('/admin/creations')

  const userActions = useMemo(
    () => getActionsBySlug(routes.cultures.slug),
    [getActionsBySlug, routes.cultures.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        field: 'name'
      },
      {
        title: t('color'),
        field: 'color',
        render: culture => (
          <Icon
            icon={ circle }
            color={ culture.color }
            style={ { justifyContent: 'start' } }
          />
        )
      }
    ],
    [t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-breeding',
        Component: BreedingModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, breeding) => {
      createDialog({
        id: 'edit-breeding',
        Component: BreedingModal,
        props: {
          breeding,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onDeleteClick = useCallback(
    (_, breeding) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'female',
            howMany: 1,
            this: t('breeding', { howMany: 1 }),
            name: breeding.name
          }),
          onConfirm: () => {
            dispatch(BreedingActions.removeBreeding(
              breeding,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('breeding', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'female', howMany: 1 }))
                }))
                mutate()
              },
              ({ response }) => {
                createDialog({
                  id: 'culture-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'female',
                      action: toLower(t('remove')),
                      this: toLower(t('breeding', { howMany: 1 }))
                    })  }.`,
                    message: `${ t(response.data.message) }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  const crumbs = useMemo(() => [
    {
      title: t('breeding', { howMany: 2 }),
      key: 'breedings-screen',
      url: '/breedings',
      active: true
    }
  ], [t])

  return (
    <Screen title={ routes.breedings.title } breadcrumbs={ crumbs }>
      <DataTable
        loading={ isValidating }
        data={ breedings || [] }
        columns={ columns }
        onAddClick={ userActions.write && onAddClick }
        onEditClick={ userActions.edit && onEditClick }
        onDeleteClick={ userActions.delete && onDeleteClick }
      />
    </Screen>
  )
}

export default BreedingsScreen
